import type {
  InternalNoteDto,
  NotificationDispatchDiagnosticsDto,
  NotificationListResult,
  NotificationPreferenceDto,
  PagedResult,
  RecentlyViewedDto,
  SavedFilterDto,
  TimelineEntryDto,
  WatchlistItemDto,
} from "@/types/api";
import { api } from "./client";

// Activity Timeline UX §1 — extra filter knobs surfaced by the History panel.
export interface TimelineQuery {
  page?: number;
  pageSize?: number;
  /** Comma-separated TimelineEntryKind names, e.g. "Note,BrokerActivity". */
  kinds?: string;
  /** Comma-separated BrokerActivityType names, e.g. "CallLogged,TaskCompleted". */
  types?: string;
  dateFrom?: string;
  dateTo?: string;
  userId?: string;
  search?: string;
}

export const timelineApi = {
  list: (entityType: string, entityId: string, query: TimelineQuery = {}) => {
    const { page = 1, pageSize = 10, ...rest } = query;
    return api.get<PagedResult<TimelineEntryDto>>(
      `timeline/${encodeURIComponent(entityType)}/${encodeURIComponent(entityId)}`,
      { page, pageSize, ...rest },
    );
  },
};

export interface CreateNotePayload {
  entityType: string;
  entityId: string;
  body: string;
}

// Legacy InternalNote CRUD — backs the cross-entity Activity Timeline +
// handoff summaries. The Notes Module v1.0 (`/api/notes`, see
// `src/pages/Notes/api/notes.ts`) is the new canonical surface.
export const notesApi = {
  list: (entityType: string, entityId: string) =>
    api.get<InternalNoteDto[]>(`internal-notes/${encodeURIComponent(entityType)}/${encodeURIComponent(entityId)}`),
  create: (payload: CreateNotePayload) => api.post<InternalNoteDto>("internal-notes", payload),
  update: (id: string, body: string) => api.put<InternalNoteDto>(`internal-notes/${id}`, { body }),
  remove: (id: string) => api.delete<void>(`internal-notes/${id}`),
};

export interface AddWatchlistPayload {
  entityType: string;
  entityId: string;
  entityTitle?: string | null;
}

export const watchlistApi = {
  list: () => api.get<WatchlistItemDto[]>("watchlist"),
  isWatching: (entityType: string, entityId: string) =>
    api.get<boolean>(`watchlist/${encodeURIComponent(entityType)}/${encodeURIComponent(entityId)}`),
  /** Batch star-status for list pages — one call per page, not one per row. */
  statuses: (entityType: string, entityIds: string[]) =>
    api.get<Record<string, boolean>>("watchlist/status", {
      entityType,
      ids: entityIds.join(","),
    }),
  add: (payload: AddWatchlistPayload) => api.post<WatchlistItemDto>("watchlist", payload),
  remove: (entityType: string, entityId: string) =>
    api.delete<void>(`watchlist/${encodeURIComponent(entityType)}/${encodeURIComponent(entityId)}`),
};

export const notificationsApi = {
  list: (unreadOnly = false, page = 1, pageSize = 25) =>
    api.get<NotificationListResult>("notifications", { unreadOnly, page, pageSize }),
  markRead: (id: string) => api.put<void>(`notifications/${id}/read`),
  markAllRead: () => api.put<number>("notifications/read-all"),
  getPreferences: () => api.get<NotificationPreferenceDto[]>("notifications/preferences"),
  updatePreferences: (preferences: NotificationPreferenceDto[]) =>
    api.put<void>("notifications/preferences", { preferences }),
  /**
   * Why staff notification email did or did not go out over a recent window.
   * ADMIN-only — the server answers 403 for anyone else, so the caller gates
   * the surface rather than letting a non-admin watch a request fail.
   */
  dispatchDiagnostics: (windowDays: number) =>
    api.get<NotificationDispatchDiagnosticsDto>("notifications/dispatch-diagnostics", { windowDays }),
};

export interface RecordViewPayload {
  entityType: string;
  entityId: string;
  entityTitle: string;
}

export const recentApi = {
  list: () => api.get<RecentlyViewedDto[]>("recent"),
  record: (payload: RecordViewPayload) => api.post<void>("recent", payload),
};

export interface UpsertSavedFilterPayload {
  pageKey: string;
  name: string;
  filterJson: string;
  isDefault: boolean;
}

export const savedFiltersApi = {
  list: (pageKey: string) => api.get<SavedFilterDto[]>("saved-filters", { pageKey }),
  create: (payload: UpsertSavedFilterPayload) => api.post<SavedFilterDto>("saved-filters", payload),
  updateJson: (id: string, filterJson: string) =>
    api.put<SavedFilterDto>(`saved-filters/${id}`, { filterJson }),
  rename: (id: string, name: string) =>
    api.put<SavedFilterDto>(`saved-filters/${id}/rename`, { name }),
  setDefault: (id: string) => api.put<SavedFilterDto>(`saved-filters/${id}/default`),
  remove: (id: string) => api.delete<void>(`saved-filters/${id}`),
};

export const handoffApi = {
  latest: (entityType: string, entityId: string) =>
    api.get<InternalNoteDto | null>(
      `internal-notes/${encodeURIComponent(entityType)}/${encodeURIComponent(entityId)}/handoff`
    ),
  acknowledge: (id: string) => api.post<InternalNoteDto>(`internal-notes/${id}/acknowledge`),
};
